// Prompt AD (2026-09-21) — Well Construction "SCOPE OF WORK": Well Type,
// Casing Size, Est. Casing Depth, Est. Total Depth, Screen, Gravel Pack,
// Cement/Seal + texto largo del alcance. Total Price y comisión del
// perforador se muestran al costado (leídos, no editables aquí).
import { useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ClipboardList, Save as SaveIcon, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const FIELDS = [
  { key: 'well_type', label: 'Well Type' },
  { key: 'casing_size', label: 'Casing Size' },
  { key: 'est_casing_depth', label: 'Est. Casing Depth' },
  { key: 'est_total_depth', label: 'Est. Total Depth' },
  { key: 'screen', label: 'Screen' },
  { key: 'gravel_pack', label: 'Gravel Pack' },
  { key: 'cement_seal', label: 'Cement/Seal' },
];

export default function ScopeOfWorkCard({ job, onSaved, commission }) {
  const scope = job.scope_of_work || {};
  const [draft, setDraft] = useState({
    well_type: scope.well_type || '', casing_size: scope.casing_size || '',
    est_casing_depth: scope.est_casing_depth || '', est_total_depth: scope.est_total_depth || '',
    screen: scope.screen || '', gravel_pack: scope.gravel_pack || '', cement_seal: scope.cement_seal || '',
    description: scope.description || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/jobs/${job.id}`, { scope_of_work: draft });
      onSaved(res.data);
      toast.success('Scope of Work guardado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-white border-gray-100 shadow-sm" data-testid="scope-of-work-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-[#3399FF]" /> Scope of Work
          </CardTitle>
          <div className="flex items-center gap-2 text-xs">
            <span className="font-semibold text-gray-700 flex items-center gap-1" data-testid="scope-of-work-total">
              <DollarSign className="w-3.5 h-3.5 text-emerald-600" /> Total: ${(job.total || 0).toLocaleString()}
            </span>
            {commission && (
              <span className="font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded" data-testid="scope-of-work-driller-commission">
                {commission.pct ? `${commission.pct} = ` : ''}{commission.amount}
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {FIELDS.map(f => (
            <div key={f.key} className="space-y-1">
              <Label className="text-[10px] text-gray-500 uppercase">{f.label}</Label>
              <Input className="h-9 text-xs" value={draft[f.key]} onChange={e => setDraft({ ...draft, [f.key]: e.target.value })} data-testid={`scope-${f.key}`} />
            </div>
          ))}
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] text-gray-500 uppercase">Scope Description</Label>
          <Textarea rows={3} className="text-sm" value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} placeholder="Describe the full scope of work..." data-testid="scope-description" />
        </div>
        <div className="flex justify-end">
          <Button size="sm" className="h-8 text-xs bg-[#003366] hover:bg-[#004080] text-white" onClick={handleSave} disabled={saving} data-testid="scope-save-btn">
            <SaveIcon className="w-3.5 h-3.5 mr-1" /> {saving ? 'Guardando…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
